/**
 * Routing. One route per nav entry, generated from the same list the sidebar renders.
 *
 * `BrowserRouter` rather than a hash router: the URL is a real path, so `/markets` can be
 * typed, bookmarked and pasted into a message. That needs the server to serve `index.html`
 * for unknown paths — Vite's dev server does it by default, and the production deployment
 * needs the same rule in Caddy. Noted here because it is the kind of thing that works for
 * the whole of development and breaks on the first deploy.
 *
 * **`/login` sits outside the shell and `RequireAuth` wraps everything else** (task 30). The
 * login screen has no sidebar and no kill switch, because there is nothing to navigate to
 * and nothing to stop; every other route is behind the gate. The gate is navigation, not
 * security — the API refuses unauthenticated requests on its own, and H9 is explicit that a
 * rule living in the client is not a rule.
 */

import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { RedirectIfSignedIn, RequireAuth } from './auth/RequireAuth';
import { LiveProvider } from './live/LiveProvider';
import { FleetScreen } from './screens/FleetScreen';
import { DesignGallery } from './screens/DesignGallery';
import { ScreenPreview } from './screens/ScreenPreview';
import { ShellPreview } from './screens/ShellPreview';
import { LoginScreen } from './screens/LoginScreen';
import { MarketsScreen } from './screens/MarketsScreen';
import { PLACEHOLDER_COPY, Placeholder } from './screens/Placeholder';
import { ResearchScreen } from './screens/ResearchScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import {
  CompanyRoute,
  CoverageRoute,
  NewsRoute,
  ScorecardRoute,
  ScreenerRoute,
  TrumpWatchRoute,
} from './screens/stocks';
import { StrategiesScreen } from './screens/StrategiesScreen';
import { SystemScreen } from './screens/SystemScreen';
import { AppShell } from './shell/AppShell';
import { DEFAULT_SCREEN, NAV_ITEMS, pathFor } from './shell/routes';

/** Screens with a real implementation. Everything else falls through to a placeholder. */
const BUILT: Record<string, () => React.JSX.Element> = {
  coverage: CoverageRoute,
  fleet: FleetScreen,
  markets: MarketsScreen,
  news: NewsRoute,
  research: ResearchScreen,
  scorecard: ScorecardRoute,
  screener: ScreenerRoute,
  settings: SettingsScreen,
  strategies: StrategiesScreen,
  system: SystemScreen,
  trump: TrumpWatchRoute,
};

export function App(): React.JSX.Element {
  return (
    <Router>
      {/* `LiveProvider` inside `AuthProvider` and outside the routes. Inside, because it opens
          the socket only once `status === 'in'` and reads that from the auth context. Outside
          the routes, because one socket serves the whole application — mounting it per screen
          would open and close a connection on every navigation, and the reconnect it is
          designed to survive would then be a thing normal use causes. */}
      <AuthProvider>
        <LiveProvider>
        <Routes>
          {/* The design system, rendered against the real bundler with no backend. Behind
              `import.meta.env.DEV` so Rollup drops both this route and the module from the
              production bundle, and outside `RequireAuth` because requiring a login to look
              at a button defeats the point of having somewhere to look. See the file's own
              docstring for why it had to exist at all. */}
          {import.meta.env.DEV ? <Route path="/__design" element={<DesignGallery />} /> : null}
          {/* The real screens against a seeded query cache — see ScreenPreview's docstring
              for why a fixture rather than a running stack. */}
          {import.meta.env.DEV ? <Route path="/__design/screens" element={<ScreenPreview />} /> : null}
          {/* 🔴 The real chrome, with no backend. Added 2026-08-05 because the shell had
              exactly one place it could be looked at — a signed-in session against a running
              API — and shipped three stacked footer bars, an invisible active nav row and a
              density control labelled `CMF · CMP · DNS` as a result. See ShellPreview's own
              docstring; the short version is that a surface nobody can render is a surface
              nobody reviews. */}
          {import.meta.env.DEV ? <Route path="/__design/shell" element={<ShellPreview />} /> : null}
          <Route
            path="/login"
            element={
              <RedirectIfSignedIn>
                <LoginScreen />
              </RedirectIfSignedIn>
            }
          />
          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
              <Route index element={<Navigate to={pathFor(DEFAULT_SCREEN)} replace />} />
              {NAV_ITEMS.map((item) => {
                const Built = BUILT[item.id];
                return (
                  <Route
                    key={item.id}
                    path={item.id}
                    element={
                      Built ? (
                        <Built />
                      ) : (
                        <Placeholder screen={item.id}>{PLACEHOLDER_COPY[item.id] ?? ''}</Placeholder>
                      )
                    }
                  />
                );
              })}
              {/* S11's tabs. One extra route rather than turning every screen's path into
                  `id/*`: thirteen screens have no sub-navigation, and a wildcard on each would
                  make `/markets/nonsense` render Markets instead of falling through to the
                  redirect below. `screenFor` already keys the sidebar off the first segment, so
                  the nav highlights System on both. */}
              <Route path="system/:tab" element={<SystemScreen />} />
              {/* The company dossier, and it nests under Screener for the same reason S11's tabs
                  nest under System: `screenFor` keys the sidebar off the first segment, so the
                  Screener row stays highlighted while a dossier is open and the shell needs no
                  extra state to know where the reader is. The segment is an **ISIN**, not a
                  ticker — a ticker belongs to a listing and is reassigned after a delisting, and
                  a bookmarked dossier has to survive both. */}
              <Route path="screener/:isin" element={<CompanyRoute />} />
              {/* A typo in the address bar lands on the dashboard, not a blank frame. */}
              <Route path="*" element={<Navigate to={pathFor(DEFAULT_SCREEN)} replace />} />
            </Route>
          </Route>
        </Routes>
        </LiveProvider>
      </AuthProvider>
    </Router>
  );
}
